import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import Button from '../components/UI/Button';
import AntiguaFlag from '../components/UI/AntiguaFlag';

const AboutPage = () => {
  const [ref1, inView1] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref2, inView2] = useInView({ threshold: 0.1, triggerOnce: true });
  const [refAnnouncements, inViewAnnouncements] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref3, inView3] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref4, inView4] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref5, inView5] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[var(--color-neutral-50)] to-[var(--color-default-background)]">
      {/* Hero Section with Animation */}
      <section className="relative overflow-hidden py-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-brand-500)]/10 via-transparent to-[var(--color-success-500)]/10 animate-pulse"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className={`text-center space-y-6 transition-all duration-1000 ${inView1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} ref={ref1}>
            <div className="flex justify-center mb-6 animate-bounce-slow">
              <div className="w-24 h-16">
                <AntiguaFlag width={96} height={64} className="rounded-lg shadow-lg" />
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-[var(--color-default-font)] mb-4 animate-fade-in">
              <span className="bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-success-600)] bg-clip-text text-transparent">
                Citizens Participation Portal
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-[var(--color-subtext-color)] max-w-3xl mx-auto leading-relaxed mb-4">
              Empowering the people of Antigua and Barbuda<br />
              to shape our communities through transparent, democratic participation.
            </p>
            <div className="relative inline-block mb-6">
              <p className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-[var(--color-success-600)] via-[var(--color-warning-500)] to-[var(--color-success-600)] bg-clip-text text-transparent animate-pulse-once">
                Participation that pays.
              </p>
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[var(--color-success-600)] to-transparent animate-pulse"></div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-2xl md:text-4xl font-bold text-[var(--color-default-font)] mb-8">
              <span className="flex items-center gap-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="text-[var(--color-brand-primary)]">Plan</span>
                <span className="text-[var(--color-neutral-400)]">|</span>
              </span>
              <span className="flex items-center gap-2 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <span className="text-[var(--color-success-600)]">Participate</span>
                <span className="text-[var(--color-neutral-400)]">|</span>
              </span>
              <span className="animate-fade-in" style={{ animationDelay: '0.5s' }}>
                <span className="text-[var(--color-warning-500)]">Prosper</span>
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to="/projects">
                <Button size="lg" className="animate-pulse-once">
                  Explore Projects
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" size="lg">
                  Join the Community
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[var(--color-default-background)] via-[var(--color-neutral-50)] to-[var(--color-default-background)] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[var(--color-brand-primary)] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[var(--color-success-600)] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className={`text-center transition-all duration-1000 ${inView2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} ref={ref2}>
            {/* Main Heading */}
            <div className="mb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-[var(--color-default-font)] mb-4">
                <span className="bg-gradient-to-r from-[var(--color-brand-primary)] via-[var(--color-success-600)] to-[var(--color-brand-primary)] bg-clip-text text-transparent animate-gradient">
                  Antigua & Barbuda
                </span>
              </h2>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-3xl md:text-5xl font-bold text-[var(--color-default-font)] mb-6">
                <span className="animate-fade-in" style={{ animationDelay: '0.2s' }}>Your Voice.</span>
                <span className="animate-fade-in" style={{ animationDelay: '0.4s' }}>Your Vote.</span>
                <span className="animate-fade-in" style={{ animationDelay: '0.6s' }}>Your Share.</span>
              </div>
            </div>

            {/* Mission Content */}
            <div className="max-w-4xl mx-auto space-y-8 text-left">
              <div className="bg-[var(--color-default-background)] rounded-3xl p-8 md:p-12 shadow-2xl border border-[var(--color-neutral-border)] backdrop-blur-sm">
                <p className="text-xl md:text-2xl text-[var(--color-subtext-color)] leading-relaxed mb-6 font-medium">
                  The Citizens Participation Portal lets Antiguans decide what gets built, funded, and prioritised — and earn from it.
                </p>
                
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4 animate-slide-in-right" style={{ animationDelay: '0.1s' }}>
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-[var(--color-brand-primary)] to-[var(--color-success-600)] rounded-full flex items-center justify-center text-white font-bold mt-1">
                      ✓
                    </div>
                    <p className="text-lg md:text-xl text-[var(--color-default-font)] font-semibold">
                      Propose projects.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-4 animate-slide-in-right" style={{ animationDelay: '0.2s' }}>
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-[var(--color-brand-primary)] to-[var(--color-success-600)] rounded-full flex items-center justify-center text-white font-bold mt-1">
                      ✓
                    </div>
                    <p className="text-lg md:text-xl text-[var(--color-default-font)] font-semibold">
                      Vote on real community needs.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-4 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-[var(--color-brand-primary)] to-[var(--color-success-600)] rounded-full flex items-center justify-center text-white font-bold mt-1">
                      ✓
                    </div>
                    <p className="text-lg md:text-xl text-[var(--color-default-font)] font-semibold">
                      Track where the money goes — transparently.
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[var(--color-brand-500)]/10 to-[var(--color-success-500)]/10 rounded-2xl p-6 md:p-8 mb-6 border-l-4 border-[var(--color-success-600)] relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--color-success-600)]/10 rounded-full blur-3xl"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-3xl">💰</span>
                      <h3 className="text-xl md:text-2xl font-bold text-[var(--color-success-600)]">Participation That Pays</h3>
                    </div>
                    <p className="text-lg md:text-xl text-[var(--color-default-font)] leading-relaxed font-medium mb-4">
                      When approved projects generate value through sustainable development and national partnerships, 
                      citizens who participate share in the returns.
                    </p>
                    <div className="grid md:grid-cols-3 gap-4 mt-4">
                      <div className="bg-[var(--color-default-background)]/50 rounded-lg p-3 border border-[var(--color-success-600)]/20">
                        <p className="text-sm font-semibold text-[var(--color-success-600)] mb-1">Propose & Vote</p>
                        <p className="text-xs text-[var(--color-subtext-color)]">Earn rewards for active participation</p>
                      </div>
                      <div className="bg-[var(--color-default-background)]/50 rounded-lg p-3 border border-[var(--color-success-600)]/20">
                        <p className="text-sm font-semibold text-[var(--color-success-600)] mb-1">Project Success</p>
                        <p className="text-xs text-[var(--color-subtext-color)]">Share in project-generated value</p>
                      </div>
                      <div className="bg-[var(--color-default-background)]/50 rounded-lg p-3 border border-[var(--color-success-600)]/20">
                        <p className="text-sm font-semibold text-[var(--color-success-600)] mb-1">Community Growth</p>
                        <p className="text-xs text-[var(--color-subtext-color)]">Benefit from sustainable development</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-2xl md:text-3xl font-bold text-[var(--color-default-font)] text-center">
                    This is modern democracy:
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                    <span className="px-6 py-3 bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-brand-600)] text-white rounded-full font-semibold text-lg shadow-lg animate-pulse-once">
                      Speak up.
                    </span>
                    <span className="px-6 py-3 bg-gradient-to-r from-[var(--color-success-600)] to-[var(--color-success-700)] text-white rounded-full font-semibold text-lg shadow-lg animate-pulse-once" style={{ animationDelay: '0.2s' }}>
                      Take part.
                    </span>
                    <span className="px-6 py-3 bg-gradient-to-r from-[var(--color-warning-500)] to-[var(--color-warning-600)] text-white rounded-full font-semibold text-lg shadow-lg animate-pulse-once" style={{ animationDelay: '0.4s' }}>
                      Benefit together.
                    </span>
                  </div>
                </div>
              </div>

              {/* Closing Statement */}
              <div className="text-center mt-8">
                <p className="text-3xl md:text-4xl font-bold text-[var(--color-default-font)] mb-2">
                  Your country shouldn't work without you.
                </p>
                <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-success-600)] bg-clip-text text-transparent">
                  Now it doesn't.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Announcements Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[var(--color-brand-500)]/10 via-[var(--color-success-500)]/10 to-[var(--color-warning-500)]/10">
        <div className="container mx-auto max-w-6xl">
          <div className={`text-center mb-16 transition-all duration-1000 ${inViewAnnouncements ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} ref={refAnnouncements}>
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-warning-500)] text-white rounded-full text-sm font-semibold mb-4 animate-pulse-once">
              🚀 COMING SOON
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--color-default-font)] mb-4">
              Upcoming Features
            </h2>
            <p className="text-xl text-[var(--color-subtext-color)] max-w-2xl mx-auto">
              Exciting new capabilities coming soon to enhance your participation experience
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: '📹',
                title: 'Public Connect & Video Broadcasts',
                description: 'Experience a new era of transparent governance with live video broadcasts and direct citizen engagement. Join real-time video sessions with government officials, participate in interactive town halls, and engage in direct discussions about policies and projects that affect your community. Ask questions, share concerns, and witness decision-making processes firsthand.',
                color: 'from-blue-500 to-cyan-500',
                badge: 'COMING SOON',
                features: [
                  'Live video broadcasts with officials',
                  'Real-time Q&A and discussions',
                  'Interactive town hall meetings',
                  'Recorded session archives',
                  'Multi-participant video calls',
                  'Screen sharing capabilities',
                ],
                benefits: 'Stay informed and engaged with government activities without leaving your home. Participate in public meetings regardless of location, ensuring your voice is heard in important community decisions.',
              },
              {
                icon: '👁️',
                title: 'Community Projects Supervision',
                description: 'Take an active role in overseeing community projects from proposal to completion. Access comprehensive project dashboards with real-time updates, detailed budget breakdowns, milestone tracking, and progress reports. Join oversight committees, review financial statements, and ensure public funds are used effectively and transparently.',
                color: 'from-purple-500 to-pink-500',
                badge: 'COMING SOON',
                features: [
                  'Real-time project monitoring',
                  'Interactive progress dashboards',
                  'Transparent budget tracking',
                  'Oversight committee participation',
                  'Financial statement access',
                  'Milestone completion alerts',
                ],
                benefits: 'Ensure accountability and transparency in public spending. Monitor project progress, verify budget allocations, and contribute to oversight efforts that keep community projects on track and within budget.',
              },
              {
                icon: '📄',
                title: 'Documents Filing to Government',
                description: 'Streamline your interactions with government services through our secure document filing system. Submit official applications, permits, and documents directly to government authorities. Track your submission status in real-time, receive digital confirmations, and maintain a complete history of all your filings. Reduce paperwork, save time, and eliminate the need for in-person visits.',
                color: 'from-green-500 to-emerald-500',
                badge: 'COMING SOON',
                features: [
                  'Secure document submission',
                  'Real-time status tracking',
                  'Digital receipt confirmations',
                  'Complete filing history',
                  'Document version control',
                  'Automated notifications',
                ],
                benefits: 'Simplify your interactions with government services. Submit documents online, track progress instantly, and receive confirmations without waiting in lines or making multiple trips to government offices.',
              },
            ].map((announcement, index) => (
              <div
                key={index}
                className={`bg-[var(--color-default-background)] rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-[var(--color-warning-500)]/30 relative overflow-hidden opacity-90 hover:opacity-100 ${
                  inViewAnnouncements ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                {/* Badge */}
                <div className="absolute top-4 right-4 bg-gradient-to-r from-[var(--color-warning-500)] to-[var(--color-brand-primary)] text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse-once shadow-lg z-30">
                  {announcement.badge}
                </div>
                
                {/* Coming Soon Overlay - visible on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-warning-500)]/90 to-[var(--color-brand-primary)]/90 backdrop-blur-sm rounded-2xl flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300 z-20">
                  <div className="text-center p-4 text-white">
                    <div className="text-5xl mb-3 animate-bounce-slow">⏳</div>
                    <p className="text-xl font-bold mb-2">Coming Soon</p>
                    <p className="text-sm opacity-90">This feature is under development</p>
                    <p className="text-xs opacity-75 mt-2">Stay tuned for updates!</p>
                  </div>
                </div>
                
                {/* Subtle overlay indicator */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[var(--color-warning-500)] via-[var(--color-brand-primary)] to-[var(--color-warning-500)] animate-pulse"></div>
                
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-1000"></div>
                
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${announcement.color} flex items-center justify-center text-3xl mb-4 animate-bounce-slow relative z-10`}>
                  {announcement.icon}
                </div>
                <h3 className="text-xl font-bold text-[var(--color-default-font)] mb-3 relative z-10">
                  {announcement.title}
                </h3>
                <p className="text-[var(--color-subtext-color)] leading-relaxed mb-4 relative z-10">
                  {announcement.description}
                </p>
                <div className="bg-[var(--color-neutral-50)] rounded-lg p-4 mb-4 relative z-10">
                  <p className="text-sm font-semibold text-[var(--color-default-font)] mb-2">Key Benefits:</p>
                  <p className="text-sm text-[var(--color-subtext-color)] leading-relaxed">
                    {announcement.benefits}
                  </p>
                </div>
                <ul className="space-y-2 relative z-10">
                  <p className="text-sm font-semibold text-[var(--color-default-font)] mb-2">Features:</p>
                  {announcement.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[var(--color-subtext-color)]">
                      <span className="text-[var(--color-success-600)] font-bold">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[var(--color-neutral-50)] to-[var(--color-default-background)]">
        <div className="container mx-auto max-w-6xl">
          <div className={`text-center mb-16 transition-all duration-1000 ${inView3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} ref={ref3}>
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--color-default-font)] mb-4">
              Platform Features
            </h2>
            <p className="text-xl text-[var(--color-subtext-color)] max-w-2xl mx-auto">
              Everything you need to participate in community decision-making
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: '🏗️',
                title: 'Project Proposals',
                description: 'Citizens can propose new community projects, share ideas, and gather support from fellow community members.',
                color: 'from-blue-500 to-cyan-500',
              },
              {
                icon: '🗳️',
                title: 'Voting System',
                description: 'Participate in open or anonymous voting sessions to determine which projects receive priority and funding.',
                color: 'from-purple-500 to-pink-500',
              },
              {
                icon: '💬',
                title: 'Community Chat',
                description: 'Engage in real-time discussions about projects, share feedback, and collaborate with other citizens.',
                color: 'from-green-500 to-emerald-500',
              },
              {
                icon: '📈',
                title: 'Progress Tracking',
                description: 'Monitor project milestones, track goals, and see real-time updates on community initiatives.',
                color: 'from-orange-500 to-red-500',
              },
              {
                icon: '🏆',
                title: 'Reputation System',
                description: 'Build your reputation as an active community member and earn recognition for your contributions.',
                color: 'from-yellow-500 to-amber-500',
              },
              {
                icon: '🔒',
                title: 'Secure & Verified',
                description: 'KYC verification ensures authentic participation while maintaining privacy and security standards.',
                color: 'from-indigo-500 to-blue-500',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className={`bg-[var(--color-default-background)] rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-[var(--color-neutral-border)] ${
                  inView3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${(index + 3) * 0.1}s` }}
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-3xl mb-4 animate-bounce-slow`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[var(--color-default-font)] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[var(--color-subtext-color)] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-[var(--color-default-background)]">
        <div className="container mx-auto max-w-6xl">
          <div className={`text-center mb-16 transition-all duration-1000 ${inView4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} ref={ref4}>
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--color-default-font)] mb-4">
              How It Works
            </h2>
            <p className="text-xl text-[var(--color-subtext-color)] max-w-2xl mx-auto">
              Simple steps to make your voice heard
            </p>
          </div>
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[var(--color-brand-primary)] via-[var(--color-success-600)] to-[var(--color-warning-500)] transform -translate-x-1/2 animate-pulse"></div>
            
            <div className="space-y-12">
              {[
                {
                  step: '1',
                  title: 'Register & Verify',
                  description: 'Create your account and complete KYC verification to become a verified citizen participant.',
                  icon: '👤',
                  side: 'left',
                },
                {
                  step: '2',
                  title: 'Explore Projects',
                  description: 'Browse active community projects, read proposals, and see what your neighbors are working on.',
                  icon: '🔍',
                  side: 'right',
                },
                {
                  step: '3',
                  title: 'Participate & Vote',
                  description: 'Join discussions, share your thoughts, and vote on projects that align with your priorities.',
                  icon: '🗳️',
                  side: 'left',
                },
                {
                  step: '4',
                  title: 'Track Impact',
                  description: 'Monitor project progress, see how your participation makes a difference, and build your reputation.',
                  icon: '📊',
                  side: 'right',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-center gap-8 ${
                    item.side === 'right' ? 'md:flex-row-reverse' : ''
                  } transition-all duration-1000 ${
                    inView4 ? 'opacity-100 translate-x-0' : item.side === 'right' ? 'opacity-0 translate-x-10' : 'opacity-0 -translate-x-10'
                  }`}
                  style={{ transitionDelay: `${index * 0.2}s` }}
                >
                  <div className="flex-1 bg-[var(--color-neutral-50)] rounded-2xl p-8 shadow-lg border border-[var(--color-neutral-border)] hover:shadow-2xl transition-shadow duration-300">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[var(--color-brand-primary)] to-[var(--color-success-600)] rounded-full flex items-center justify-center text-white text-2xl font-bold animate-pulse-once">
                        {item.step}
                      </div>
                      <div className="text-4xl">{item.icon}</div>
                    </div>
                    <h3 className="text-2xl font-bold text-[var(--color-default-font)] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-[var(--color-subtext-color)] leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block w-12 h-12 bg-[var(--color-brand-primary)] rounded-full flex items-center justify-center text-white font-bold animate-bounce-slow">
                      ↓
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[var(--color-neutral-50)] to-[var(--color-default-background)]">
        <div className="container mx-auto max-w-6xl">
          <div className={`text-center mb-16 transition-all duration-1000 ${inView5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} ref={ref5}>
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--color-default-font)] mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-[var(--color-subtext-color)] max-w-2xl mx-auto">
              Principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: '🔓', title: 'Transparency', description: 'Open and honest communication' },
              { icon: '🤝', title: 'Inclusivity', description: 'Every voice matters' },
              { icon: '⚖️', title: 'Fairness', description: 'Equal opportunity for all' },
              { icon: '🛡️', title: 'Security', description: 'Your data is protected' },
            ].map((value, index) => (
              <div
                key={index}
                className={`text-center p-6 bg-[var(--color-default-background)] rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 border border-[var(--color-neutral-border)] ${
                  inView5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="text-5xl mb-4 animate-bounce-slow">{value.icon}</div>
                <h3 className="text-xl font-bold text-[var(--color-default-font)] mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-[var(--color-subtext-color)]">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[var(--color-default-background)] to-[var(--color-success-50)]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-3 bg-gradient-to-r from-[var(--color-success-600)] to-[var(--color-warning-500)] text-white rounded-full text-lg font-bold mb-6 animate-pulse-once">
              💰 Earn While You Participate
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--color-default-font)] mb-4">
              Your Participation, Your Share
            </h2>
            <p className="text-xl text-[var(--color-subtext-color)] max-w-3xl mx-auto">
              When community projects succeed, everyone who participated benefits. This is democracy that rewards engagement.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-[var(--color-default-background)] rounded-2xl p-6 shadow-lg border-2 border-[var(--color-success-600)]/20 hover:border-[var(--color-success-600)] transition-all duration-300">
              <div className="text-5xl mb-4 text-center">📋</div>
              <h3 className="text-xl font-bold text-[var(--color-default-font)] mb-3 text-center">Plan Projects</h3>
              <p className="text-[var(--color-subtext-color)] text-center mb-4">
                Propose community initiatives that matter to you
              </p>
              <div className="bg-[var(--color-success-50)] rounded-lg p-3 text-center">
                <p className="text-sm font-semibold text-[var(--color-success-700)]">Earn proposal rewards</p>
              </div>
            </div>
            
            <div className="bg-[var(--color-default-background)] rounded-2xl p-6 shadow-lg border-2 border-[var(--color-success-600)]/20 hover:border-[var(--color-success-600)] transition-all duration-300">
              <div className="text-5xl mb-4 text-center">🗳️</div>
              <h3 className="text-xl font-bold text-[var(--color-default-font)] mb-3 text-center">Participate Actively</h3>
              <p className="text-[var(--color-subtext-color)] text-center mb-4">
                Vote, discuss, and engage with community projects
              </p>
              <div className="bg-[var(--color-success-50)] rounded-lg p-3 text-center">
                <p className="text-sm font-semibold text-[var(--color-success-700)]">Build participation credits</p>
              </div>
            </div>
            
            <div className="bg-[var(--color-default-background)] rounded-2xl p-6 shadow-lg border-2 border-[var(--color-warning-500)]/20 hover:border-[var(--color-warning-500)] transition-all duration-300">
              <div className="text-5xl mb-4 text-center">💵</div>
              <h3 className="text-xl font-bold text-[var(--color-default-font)] mb-3 text-center">Prosper Together</h3>
              <p className="text-[var(--color-subtext-color)] text-center mb-4">
                Share in the financial returns from successful projects
              </p>
              <div className="bg-[var(--color-warning-50)] rounded-lg p-3 text-center">
                <p className="text-sm font-semibold text-[var(--color-warning-700)]">Receive your share</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[var(--color-success-600)] to-[var(--color-brand-primary)] rounded-3xl p-8 md:p-12 text-white text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h3>
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div>
                <div className="text-4xl mb-2">1️⃣</div>
                <p className="font-semibold">Propose</p>
                <p className="text-sm opacity-90">Submit project ideas</p>
              </div>
              <div>
                <div className="text-4xl mb-2">2️⃣</div>
                <p className="font-semibold">Vote</p>
                <p className="text-sm opacity-90">Participate in decisions</p>
              </div>
              <div>
                <div className="text-4xl mb-2">3️⃣</div>
                <p className="font-semibold">Track</p>
                <p className="text-sm opacity-90">Monitor project progress</p>
              </div>
              <div>
                <div className="text-4xl mb-2">4️⃣</div>
                <p className="font-semibold">Earn</p>
                <p className="text-sm opacity-90">Receive your share</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-success-600)] text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Ready to Make a Difference?
            </h2>
            <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
              Join thousands of citizens who are actively shaping the future of Antigua and Barbuda — and earning from it.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to="/register">
                <Button size="lg" variant="secondary" className="bg-white text-[var(--color-brand-primary)] hover:bg-gray-100">
                  Get Started Today
                </Button>
              </Link>
              <Link to="/projects">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Browse Projects
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
